import { useId } from 'react'
import type { ReactNode } from 'react'

export type PanelWeight = 'hero' | 'default' | 'quiet'

interface PanelProps {
  title: string
  /** Right side of the header: freshness stamps, counts, a filter control. */
  meta?: ReactNode
  /**
   * Visual weight. The grid decides area; this decides how loudly the panel
   * announces itself. Only one `hero` per screen — if everything is a hero,
   * nothing is.
   */
  weight?: PanelWeight
  /** Applied to the <section>: grid placement belongs to the caller. */
  className?: string
  /** Body padding is opt-out so tables can bleed to the panel edge. */
  flush?: boolean
  children: ReactNode
}

const HEADING = {
  hero: 'text-base font-medium text-fg',
  default: 'text-micro font-medium uppercase tracking-label text-fg-secondary',
  quiet: 'text-micro font-medium uppercase tracking-label text-fg-muted',
} as const

/**
 * A panel is a hairline-separated cell of the console grid.
 *
 * No border of its own and no shadow: the grid's 1px gap over a line-coloured
 * background draws every separator, which keeps the surface flat and avoids
 * doubling a border with a shadow anywhere in the app.
 */
export function Panel({
  title,
  meta,
  weight = 'default',
  className = '',
  flush = false,
  children,
}: PanelProps) {
  const headingId = useId()

  return (
    <section
      aria-labelledby={headingId}
      className={`flex min-w-0 flex-col bg-surface ${className}`}
    >
      <header className="flex min-h-8 shrink-0 items-center justify-between gap-3 border-b border-line-subtle px-3 py-1.5">
        <h2 id={headingId} className={HEADING[weight]}>
          {title}
        </h2>
        {meta !== undefined && (
          <div className="flex shrink-0 items-center gap-2 text-micro text-fg-muted">{meta}</div>
        )}
      </header>
      <div className={`min-h-0 flex-1 ${flush ? '' : 'p-3'}`}>{children}</div>
    </section>
  )
}
